import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, linkedSignal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountType } from "@front/m19-api-client";
import { AccountSelectionService, TacosStrategiesService } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { parseIdFromParam } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { SbWizardPageComponent } from "@m19-board/strategies/sb-create-strategy/sb-wizard-page/sb-wizard-page.component";
import { SbWizardCreationService } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-creation.service";
import { createSbWizardForm } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form";
import {
  algoConfigFromStrategy,
  assetsAfterBrandOrTypeChange,
  assetsDefaultFormModel,
  creativesSbAsinsDefaultFormModel,
  formatDefaultFormModel,
  setupDefaultFormModel,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form.defaults";
import {
  SB_STRATEGIES_URL,
  SB_WIZARD_FORM_STEP,
  SbWizardFormModel,
  TACOS_STRATEGIES_URL,
} from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-types";
import { combineLatest, map, of, switchMap } from "rxjs";

@Component({
  selector: "app-sb-new-ad-line-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SbWizardCreationService],
  imports: [SbWizardPageComponent],
  template: `
    <app-sb-wizard-page
      [form]="creationForm"
      [steps]="steps"
      [ready]="strategyLoadReady()"
      [breadcrumbLink]="breadcrumbLink()"
      [strategyName]="loadedStrategyName()"
      [disableStoreSpotlight]="disableStoreSpotlight()"
      [tacosSpAsins]="tacosSpAsinList()"
      submitLabelKey="sb-page.create_ad_line"
      (cancelled)="leave()"
    />
  `,
})
export class SbNewAdLinePage {
  private readonly sbWizardCreationService = inject(SbWizardCreationService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly steps = [SB_WIZARD_FORM_STEP.format, SB_WIZARD_FORM_STEP.assets, SB_WIZARD_FORM_STEP.creativesSbAsins];

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  private readonly strategyId$ = this.route.paramMap.pipe(map((params) => parseIdFromParam(params.get("strategyId"))));
  private readonly tacosStrategyGroupId$ = this.route.paramMap.pipe(
    map((params) => parseIdFromParam(params.get("tacosId"))),
  );
  private readonly strategyId = toSignal(this.strategyId$, { requireSync: true });
  private readonly tacosStrategyGroupId = toSignal(this.tacosStrategyGroupId$, { requireSync: true });

  public formContext = toSignal(
    combineLatest([this.strategyId$, this.tacosStrategyGroupId$]).pipe(
      switchMap(([strategyId, tacosStrategyGroupId]) => {
        const id = tacosStrategyGroupId ?? strategyId;
        if (id == null) return of(null);
        return this.sbWizardCreationService.loadContext({ strategyId: id, fromTacos: tacosStrategyGroupId !== null });
      }),
    ),
    { initialValue: null },
  );

  readonly tacosSpAsinList = toSignal(
    this.tacosStrategyGroupId$.pipe(
      switchMap((tacosStrategyGroupId) => {
        if (tacosStrategyGroupId == null) return of(undefined);
        return this.tacosStrategiesService.getTacosSPAsins(
          this.accountMarketplace().accountId,
          this.accountMarketplace().marketplace,
          tacosStrategyGroupId,
        );
      }),
    ),
  );

  readonly strategyLoadReady = computed(() => this.formContext() != null);
  readonly loadedStrategyName = computed(() => this.formContext()?.strategy.name ?? "");
  readonly disableStoreSpotlight = computed(() => this.tacosStrategyGroupId() != null);
  readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);
  readonly breadcrumbLink = computed((): (string | number)[] | null => {
    const tacosId = this.tacosStrategyGroupId();
    if (tacosId != null) return [TACOS_STRATEGIES_URL, tacosId];
    const strategyId = this.formContext()?.strategy.strategyId;
    if (strategyId != null) return [SB_STRATEGIES_URL, strategyId, "ad-lines"];
    return null;
  });

  private readonly formModel = linkedSignal<SbWizardFormModel>(() => {
    const context = this.formContext();
    if (!context) {
      return {
        setup: setupDefaultFormModel,
        format: formatDefaultFormModel,
        assets: assetsDefaultFormModel,
        creativesSbAsins: creativesSbAsinsDefaultFormModel,
      };
    }

    const isVendor = this.isVendor();
    return {
      setup: {
        name: context.strategy.name ?? "",
        brand: context.brand,
        algo: algoConfigFromStrategy(context.strategy),
      },
      format: formatDefaultFormModel,
      assets: assetsAfterBrandOrTypeChange(context.brand, isVendor),
      creativesSbAsins: creativesSbAsinsDefaultFormModel,
    };
  });

  readonly creationForm = createSbWizardForm({
    formModel: this.formModel,
    isVendor: this.isVendor,
    marketplace: computed(() => this.accountMarketplace().marketplace),
    submitForm: async () => this.submit(),
  });

  submit() {
    const strategy = this.formContext()?.strategy;
    if (!strategy) return;

    this.sbWizardCreationService
      .createCreativeWithAsins(this.creationForm, strategy)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("sb-form-creative.sb_creative_created"));
          this.leave();
        },
        error: (err) => {
          this.toasterService.error(err);
        },
      });
  }

  leave(): void {
    const tacosId = this.tacosStrategyGroupId();
    if (tacosId != null) {
      void this.router.navigate([TACOS_STRATEGIES_URL, tacosId]);
      return;
    }
    const strategyId = this.strategyId() ?? this.formContext()?.strategy.strategyId;
    if (strategyId != null) {
      void this.router.navigate([SB_STRATEGIES_URL, strategyId, "ad-lines"]);
      return;
    }
    void this.router.navigate([SB_STRATEGIES_URL]);
  }
}
